import logo from '../images/logo.png';
import {getAllBranchUrlNames} from './branch.helper';
import {
  BRANCH_MAPPING,
  CLOSED_TIME,
  IMAGE_SIZE,
  PAGE_DES,
  RESERVED_WORDS,
  UNIT_PRICING_UNITS,
} from '../constants/constants';
import {reduceGroupImageSize} from './image.helper';

// Builds a FAQPage JSON-LD schema straight from the same {question, answer} items that
// render the page's own visible FAQ list, so the schema can never drift from what's on the
// page — one source of truth, not a hand-copied duplicate (see SEO metadata & FAQPage
// schema doc for /jj-pgp and /halal).
export const getFaqPageSchema = faqItems => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map(item => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
});

export const getHeadTags = () => {
  // about ogType: http://ogp.me/#types
  // https://webcode.tools/open-graph-generator/product
  const meta = [
    {charset: 'utf-8'},
    {
      name: 'description',
      content: PAGE_DES.DESCRIPTION,
    },
    {
      name: 'keywords',
      contents:
        'Burger restaurant supplier, chicken shop supplier, fish and chip shop supplier, pizza shop supplier throughout the UK cooking oil, frying oils, fish and chips, packaging, curries, gravies, pickles, sauces, wholesaler, cash and carry',
    },
    {property: 'og:site_name', content: PAGE_DES.TITLE},
    {property: 'og:image', content: logo},
    {property: 'og:locale', content: 'en_UK'},
    {
      property: 'og:title',
      content: PAGE_DES.TITLE,
    },
    {property: 'og:description', content: PAGE_DES.DESCRIPTION},
    {property: 'og:card', content: 'summary'},
    {property: 'og:site', content: '@jjfoodservice'},
    {property: 'og:creator', content: '@jjfoodservice'},

    {property: 'og:type', content: 'product'},
  ];

  return {
    titleTemplate: `${PAGE_DES.TITLE} %s`,
    title: PAGE_DES.TITLE_CONTENT,
    meta,
  };
};

export const setCmsHeadTags = tags => {
  if (tags.pageTitle) {
    document.title = tags.pageTitle;
    const ogTitleElement = document.querySelector('meta[property="og:title"]');
    if (ogTitleElement) {
      ogTitleElement.setAttribute('content', tags.pageTitle);
    }
  }
  const descriptionElement = document.querySelector('meta[name="description"]');
  const ogDescriptionElement = document.querySelector(
    'meta[property="og:description"]'
  );
  if (descriptionElement && tags.metaDescription) {
    descriptionElement.setAttribute('content', tags.metaDescription);
  }
  if (ogDescriptionElement && tags.metaDescription) {
    ogDescriptionElement.setAttribute('content', tags.metaDescription);
  }
};

export const setPageHeadTags = tags => {
  if (tags.title) {
    document.title = tags.title;
    const ogTitleElement = document.querySelector('meta[property="og:title"]');
    if (ogTitleElement) {
      ogTitleElement.setAttribute('content', tags.title);
    }
  }
  const descriptionElement = document.querySelector('meta[name="description"]');
  const ogDescriptionElement = document.querySelector(
    'meta[property="og:description"]'
  );
  if (descriptionElement && tags.description) {
    descriptionElement.setAttribute('content', tags.description);
  }
  if (ogDescriptionElement && tags.description) {
    ogDescriptionElement.setAttribute('content', tags.description);
  }
};

const NO_INFO_VALUES = [
  'No information',
  'N/A',
  'No usage information is available',
];

const validStr = val => (val && !NO_INFO_VALUES.includes(val) ? val : null);

const SIZE_PATTERN = /^(?:(\d+)\s*x\s*)?(\d+(?:\.\d+)?)\s*([a-z'`]*)$/i;

// "2x4.5kg" -> {value: 9, unit: 'kg'}; null when the pack size can't be read reliably.
export const parseUnitPricingMeasure = sizeOrCut => {
  const match = validStr(sizeOrCut) && sizeOrCut.trim().match(SIZE_PATTERN);
  if (!match) {
    return null;
  }
  const [, packs = '1', quantity, rawUnit] = match;
  const unit = UNIT_PRICING_UNITS[rawUnit.toLowerCase()];
  const value = Number(
    (Number(packs) * Number(quantity) * (unit ? unit.factor : 0)).toFixed(3)
  );
  return unit && value > 0 ? {value, unit: unit.base} : null;
};

export const getOgData = product => {
  if (!product) {
    return {
      og: false,
    };
  }
  const categories = !!product.categoryName
    ? product.categoryName.split(',')
    : '';
  let category;
  if (categories.length > 0) {
    category = categories[categories.length - 1];
  } else {
    category = product.categoryName;
  }
  const description = `Buy ${category} at wholesale prices from JJ Foodservice. Order online or at one of our branches today. Open to all.`;
  let collectionPrice = product.collection && product.collection.price;
  if (collectionPrice == null) {
    collectionPrice = product.price;
  }
  let deliveryPrice = product.delivery && product.delivery.price;
  if (deliveryPrice == null) {
    deliveryPrice = product.price;
  }
  // Product page UI uses small/medium thumbnails, but Google Merchant Center
  // requires images >= 1024px on the longest side — re-request the same
  // Cloudinary/Fastly assets at a larger size specifically for the schema.
  const schemaImageLinks =
    product.imageLinks &&
    product.imageLinks.length > 0 &&
    reduceGroupImageSize(product.imageLinks, false, IMAGE_SIZE.SCHEMA);
  return {
    og: true,
    title: `Buy ${product.itemName} - Order Online From JJ Foodservice`,
    image: schemaImageLinks && schemaImageLinks[0],
    imageLinks: schemaImageLinks,
    description,
    itemDescription:
      (product.productDescription &&
        product.productDescription.toLowerCase() !==
          RESERVED_WORDS.NOT_AVAILABLE &&
        product.productDescription) ||
      description,
    category: product.categoryName,
    product_type: (categories || []).join(' > ').replaceAll(',', ''),
    amount: product.price,
    collectionPrice,
    deliveryPrice,
    itemName: product.itemName,
    sku: product.itemId,
    gtin: product.gtin,
    brand: product.brand,
    branches: product.branches,
    priceValidUntil: product.offer && product.offer.promoEndIso,
    ...(product.itemNetWeight && {
      weight: {
        '@type': 'QuantitativeValue',
        value: product.itemNetWeight,
        unitCode: 'KGM',
      },
    }),
    averageScore: product.averageScore,
    reviewCount: product.reviewCount,
    isAvailable: product.isAvailable,
    sizeOrCut: product.sizeOrCut || null,
    unitPricingMeasure: parseUnitPricingMeasure(product.sizeOrCut),
    category1Name: product.category1Name || null,
    videos:
      product.video && product.video.filter(v => v && v.videoURL).length > 0
        ? product.video.filter(v => v && v.videoURL)
        : null,
    productFeatures:
      product.productFeatures && product.productFeatures.length > 0
        ? product.productFeatures
        : null,
    origin: validStr(product.origin),
    storageCondition: validStr(product.storageCondition),
    ingredients: validStr(product.ingredients),
    energyKJ: product.energyKJ > 0 ? product.energyKJ : null,
    energyKCAL: product.energyKCAL > 0 ? product.energyKCAL : null,
    fatG: validStr(String(product.fatG)),
    fatSaturatesG: validStr(String(product.fatSaturatesG)),
    carbohydrateG: validStr(String(product.carbohydrateG)),
    carbohydrateSugarsG: validStr(String(product.carbohydrateSugarsG)),
    proteinG: validStr(String(product.proteinG)),
    sodiumG: validStr(String(product.sodiumG)),
    deliveryStep: product.delivery && product.delivery.step,
    deliveryMax: product.delivery && product.delivery.max,
    collectionStep: product.collection && product.collection.step,
    collectionMax: product.collection && product.collection.max,
    deliveryMultiBuy:
      product.deliveryMultiBuy && product.deliveryMultiBuy.length > 0
        ? product.deliveryMultiBuy
        : null,
    collectionMultiBuy:
      product.collectionMultiBuy && product.collectionMultiBuy.length > 0
        ? product.collectionMultiBuy
        : null,
  };
};

export const getSchemaOpeningTimes = openingTimes =>
  openingTimes
    .slice(0, 7)
    .map((day, i) => {
      const isOpen = day && day.from !== CLOSED_TIME && day.to !== CLOSED_TIME;
      return (
        isOpen && {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: [day.day],
          opens: day.from,
          closes: day.to,
        }
      );
    })
    .filter(item => typeof item === 'object');

const makeEligibleQuantity = (step, max, min = 1) => ({
  '@type': 'QuantitativeValue',
  minValue: min,
  ...(max && {maxValue: max}),
  ...(step && step !== 1 && {stepValue: step}),
});

export const getSeoOffers = (og, baseUrl) => {
  if (og && og.branches && og.branches.length > 0) {
    try {
      const englandUrl = `${baseUrl}/product/england/${og.sku}/`;
      // Every branch with a URL name that stocks the product, so one added to the
      // branches file gets offers without a code change.
      const stockingBranches = Object.entries(
        getAllBranchUrlNames()
      ).filter(([locationId]) =>
        og.branches.some(item => item.locationId === locationId)
      );
      // Read from og.branches, which is the same on every branch URL of the product,
      // never from og.isAvailable, which is scoped to the branch in the URL being
      // rendered. Stamping that page-local value on every branch's offer made the URLs
      // contradict each other: the Manchester page said the Basingstoke offer was
      // InStock, Google followed its URL, found "Out of stock" and disapproved the item
      // for mismatched availability.
      //
      // Only stocking branches get offers, so every offer URL lands on a page that
      // agrees with it, and all offers share one availability value — mixed values
      // leave Merchant Center unable to resolve one ("Missing value [availability]").
      const isProductAvailable = stockingBranches.length > 0;
      const availability = isProductAvailable
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock';

      const branchOffers = stockingBranches
        .map(([locationId, locationName]) => {
          // The branch-name form, not the raw locationId — a branch code in the URL
          // does not resolve to a branch and is redirected, so publishing it here
          // hands Google an offer URL that has to bounce before it lands.
          const offerUrl = `${baseUrl}/product/${locationName}/${og.sku}/`;
          const commonProps = {
            priceCurrency: 'GBP',
            itemCondition: 'https://schema.org/NewCondition',
            priceValidUntil: og.priceValidUntil,
          };

          return [
            {
              '@type': 'Offer',
              seller: {
                '@type': 'Organization',
                name: `JJ Foodservice - ${locationName} - Collection`,
              },
              availableAtOrFrom: {
                '@type': 'LocalBusiness',
                branchCode: locationId,
                name: `JJ Foodservice - ${locationName}`,
              },
              ...commonProps,
              price: og.collectionPrice ?? 0,
              availability,
              url: offerUrl,
              ...(og.collectionStep || og.collectionMax
                ? {
                    eligibleQuantity: makeEligibleQuantity(
                      og.collectionStep,
                      og.collectionMax
                    ),
                  }
                : {}),
            },
            {
              '@type': 'Offer',
              seller: {
                '@type': 'Organization',
                name: `JJ Foodservice - ${locationName} - Delivery`,
              },
              availableAtOrFrom: {
                '@type': 'LocalBusiness',
                branchCode: locationId,
                name: `JJ Foodservice - ${locationName}`,
              },
              ...commonProps,
              price: og.deliveryPrice ?? 0,
              availability,
              url: offerUrl,
              ...(og.deliveryStep || og.deliveryMax
                ? {
                    eligibleQuantity: makeEligibleQuantity(
                      og.deliveryStep,
                      og.deliveryMax
                    ),
                  }
                : {}),
            },
          ];
        })
        .flat();

      // No named branch stocks it: a single OutOfStock offer on the canonical URL, so
      // the product still carries an availability instead of none.
      if (!isProductAvailable) {
        branchOffers.push({
          '@type': 'Offer',
          seller: {'@type': 'Organization', name: 'JJ Foodservice'},
          priceCurrency: 'GBP',
          itemCondition: 'https://schema.org/NewCondition',
          priceValidUntil: og.priceValidUntil,
          price: og.deliveryPrice ?? og.collectionPrice ?? 0,
          availability,
          url: englandUrl,
        });
      }

      // Multi-buy tier offers (bulk pricing)
      const multiBuyOffers = [];
      (og.collectionMultiBuy || []).forEach(tier => {
        multiBuyOffers.push({
          '@type': 'Offer',
          seller: {
            '@type': 'Organization',
            name: 'JJ Foodservice - Collection',
          },
          priceCurrency: 'GBP',
          price: tier.price,
          itemCondition: 'https://schema.org/NewCondition',
          availability,
          eligibleQuantity: makeEligibleQuantity(tier.step, null, tier.min),
          url: englandUrl,
        });
      });
      (og.deliveryMultiBuy || []).forEach(tier => {
        multiBuyOffers.push({
          '@type': 'Offer',
          seller: {'@type': 'Organization', name: 'JJ Foodservice - Delivery'},
          priceCurrency: 'GBP',
          price: tier.price,
          itemCondition: 'https://schema.org/NewCondition',
          availability,
          eligibleQuantity: makeEligibleQuantity(tier.step, null, tier.min),
          url: englandUrl,
        });
      });

      return [...branchOffers, ...multiBuyOffers].map(offer => {
        if (!offer.priceValidUntil) {
          delete offer.priceValidUntil;
        }
        // Google reads unit_pricing_measure / unit_pricing_base_measure from here;
        // it's legally required in the UK for goods sold by weight or volume.
        if (og.unitPricingMeasure) {
          offer.priceSpecification = {
            '@type': 'UnitPriceSpecification',
            price: offer.price,
            priceCurrency: offer.priceCurrency,
            referenceQuantity: {
              '@type': 'QuantitativeValue',
              value: og.unitPricingMeasure.value,
              unitCode: og.unitPricingMeasure.unit,
              valueReference: {
                '@type': 'QuantitativeValue',
                value: 1,
                unitCode: og.unitPricingMeasure.unit,
              },
            },
          };
        }
        return offer;
      });
    } catch (e) {
      return null;
    }
  }
};

export const getSeoProductSchema = (og, offers, url, baseUrl) => {
  if (!og) {
    return null;
  }

  const additionalProperties = [];
  (og.productFeatures || []).forEach(f => {
    additionalProperties.push({
      '@type': 'PropertyValue',
      name: 'Feature',
      value: f,
    });
  });
  if (og.storageCondition) {
    additionalProperties.push({
      '@type': 'PropertyValue',
      name: 'Storage Condition',
      value: og.storageCondition,
    });
  }
  if (og.origin) {
    additionalProperties.push({
      '@type': 'PropertyValue',
      name: 'Country of Origin',
      value: og.origin,
    });
  }
  if (og.ingredients) {
    additionalProperties.push({
      '@type': 'PropertyValue',
      name: 'Ingredients',
      value: og.ingredients,
    });
  }

  const nutritionInfo = og.energyKCAL
    ? {
        '@type': 'NutritionInformation',
        calories: `${og.energyKCAL} kcal`,
        ...(og.fatG && {fatContent: `${og.fatG} g`}),
        ...(og.fatSaturatesG && {saturatedFatContent: `${og.fatSaturatesG} g`}),
        ...(og.carbohydrateG && {carbohydrateContent: `${og.carbohydrateG} g`}),
        ...(og.carbohydrateSugarsG && {
          sugarContent: `${og.carbohydrateSugarsG} g`,
        }),
        ...(og.proteinG && {proteinContent: `${og.proteinG} g`}),
        ...(og.sodiumG && {sodiumContent: `${og.sodiumG} g`}),
      }
    : null;

  const images =
    og.imageLinks && og.imageLinks.length > 0 ? og.imageLinks : og.image || '';

  return {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: og.itemName,
    image: images,
    description: og.itemDescription,
    sku: og.sku,
    gtin: og.gtin,
    brand: {
      '@type': 'Brand',
      name: og.brand,
    },
    ...(og.weight && {weight: og.weight}),
    ...(og.sizeOrCut && {size: og.sizeOrCut}),
    ...(og.category1Name && {category: og.category1Name}),
    ...(og.reviewCount > 0 && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: og.averageScore,
        reviewCount: og.reviewCount,
        bestRating: 5,
        worstRating: 1,
      },
    }),
    ...(additionalProperties.length > 0 && {
      additionalProperty: additionalProperties,
    }),
    ...(nutritionInfo && {nutrition: nutritionInfo}),
    offers,
    url: `${baseUrl}${url.startsWith('/') ? url : '/' + url}`,
  };
};
